import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  Row,
  type Table as ReactTable,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table';
import {
  CSSProperties,
  DetailedHTMLProps,
  memo,
  ReactNode,
  RefAttributes,
  TableHTMLAttributes,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Menu, MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, MenuItem, MenuItemDelete, Popover, Text } from '@/components/core';
import { CopyIcon, DragHandleIcon, OverflowIcon } from '@/components/icons';
import { useAutoSaveColumnSizes } from '@/hooks';
import { Id, TableRowProps } from '@/utils';

export type RowOptions<TRow> = {
  options?: {
    onAction: (row: Row<TRow>) => void;
    children: ReactNode;
  }[];
  duplicateRowAction?: (row: Row<TRow>) => void;
  deleteRowAction?: (row: Row<TRow>) => void;
};

type DraggableRowProps<TRow> = TableRowProps &
  RowOptions<TRow> & {
    row: Row<TRow>;
    isInnerTable?: boolean;
    hideDragHandle?: boolean;
    hideRowOptions?: boolean;
    props?: { cell?: React.TdHTMLAttributes<HTMLTableCellElement> };
  };

const IDraggableRow = <TRow extends Id>({
  row,
  isInnerTable,
  hideDragHandle,
  hideRowOptions,
  options,
  duplicateRowAction,
  deleteRowAction,
  className,
  props,
  ...rest
}: DraggableRowProps<TRow>) => {
  const { attributes, listeners } = useSortable({ id: row.id });
  const { transform, transition, setNodeRef, isDragging } = useSortable({
    id: row.original.id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  const { className: cellClassName, ...cellProps } = props?.cell ?? {};

  return (
    <tr
      className={merge(
        'relative',
        isDragging ? 'z-10 opacity-80' : 'z-0 opacity-100',
        isInnerTable ? 'group/row-inner' : 'group/row-outer',
        className
      )}
      ref={setNodeRef}
      style={style}
      {...rest}
    >
      {!hideDragHandle ? (
        <td className="max-w-0">
          <button
            {...attributes}
            {...listeners}
            className="group/button px-xs flex -translate-x-[100%] cursor-pointer items-center"
            tabIndex={-1}
          >
            <DragHandleIcon
              className={merge(
                'text-transparent',
                isInnerTable ? 'group-hover/row-inner:!text-gray-400' : 'group-hover/row-outer:!text-gray-400'
              )}
            />
          </button>
        </td>
      ) : null}
      {row.getVisibleCells().map((cell, index) => (
        <td
          className={cellClassName}
          key={cell.id}
          style={{ width: cell.column.getSize() }}
          aria-colindex={index}
          {...cellProps}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
      ))}
      {!hideRowOptions ? (
        <td className="max-w-0">
          <MenuTrigger>
            <Button className="px-xs p-0" variant="plain" excludeFromTabOrder>
              <OverflowIcon
                className={merge(
                  'text-transparent',
                  isInnerTable ? 'group-hover/row-inner:!text-gray-400' : 'group-hover/row-outer:!text-gray-400'
                )}
              />
            </Button>
            <Popover>
              <Menu className="outline-none">
                {options?.map((option, i) => (
                  <MenuItem key={i} onAction={() => option.onAction(row)}>
                    {option.children}
                  </MenuItem>
                ))}
                {duplicateRowAction ? (
                  <MenuItem onAction={() => duplicateRowAction(row)}>
                    <CopyIcon />
                    <Text>Duplicate</Text>
                  </MenuItem>
                ) : null}
                {deleteRowAction ? <MenuItemDelete onAction={() => deleteRowAction(row)} /> : null}
              </Menu>
            </Popover>
          </MenuTrigger>
        </td>
      ) : null}
    </tr>
  );
};

export const DraggableRow = memo(IDraggableRow) as typeof IDraggableRow;

export type TableRef<TRow> = {
  table: ReactTable<TRow>;
  remountSortableContext: () => void;
};

export type TableProps<TRow> = Omit<DetailedHTMLProps<TableHTMLAttributes<HTMLTableElement>, HTMLTableElement>, 'ref'> &
  RowOptions<TRow> &
  RefAttributes<TableRef<TRow>> & {
    columns: ColumnDef<TRow>[];
    data: TRow[];
    isInnerTable?: boolean;
    onRowIndexChange?: (oldIndex: number, newIndex: number) => void;
    hideHeader?: boolean;
    hideRowOptions?: boolean;
    hideDragHandle?: boolean;
    isResizeable?: boolean;
    props?: {
      row?: TableRowProps;
      cell?: React.TdHTMLAttributes<HTMLTableCellElement>;
      header?: React.ThHTMLAttributes<HTMLTableCellElement>;
    };
  };

const ITable = <TRow extends Id>({
  data,
  isInnerTable,
  columns,
  onRowIndexChange,
  hideHeader,
  hideRowOptions,
  hideDragHandle,
  isResizeable,
  options,
  duplicateRowAction,
  deleteRowAction,
  className,
  props,
  ref,
  ...rest
}: TableProps<TRow>) => {
  const [sortableContextKey, setSortableContextKey] = useState(0);

  const dataIds = useMemo<UniqueIdentifier[]>(() => data?.map(({ id }) => id), [data]);

  const reactTableProps: TableOptions<TRow> = {
    columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  };

  if (isResizeable) {
    reactTableProps.enableColumnResizing = true;
    reactTableProps.columnResizeMode = 'onChange';
    reactTableProps.columnResizeDirection = 'ltr';
  }

  const table = useReactTable(reactTableProps);

  useAutoSaveColumnSizes({ table, enabled: isResizeable });

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = dataIds.indexOf(active.id);
        const newIndex = dataIds.indexOf(over.id);
        onRowIndexChange?.(oldIndex, newIndex);
      }
    },
    [dataIds, onRowIndexChange]
  );

  useImperativeHandle(ref, () => {
    return {
      remountSortableContext() {
        setSortableContextKey(sortableContextKey + 1);
      },
      table,
    };
  }, [sortableContextKey, table]);

  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}));
  const { className: rowClassName, ...rowProps } = props?.row ?? {};
  const { className: headerClassName, ...headerProps } = props?.header ?? {};

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
      onDragStart={() => setSortableContextKey((prev) => prev + 1)}
      sensors={sensors}
    >
      <table
        className={merge('group/table', className)}
        style={isResizeable ? { width: table.getCenterTotalSize() } : undefined}
        {...rest}
      >
        {!hideHeader ? (
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {!hideDragHandle ? <th className="w-0" /> : null}
                {headerGroup.headers.map((header, index) => (
                  <th
                    className={merge('group/th relative text-left', headerClassName)}
                    colSpan={header.colSpan}
                    style={{ width: header.getSize() }}
                    key={header.id}
                    aria-colindex={index}
                    {...headerProps}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}

                    {isResizeable ? (
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className="absolute top-0 right-0 z-50 h-full w-4 translate-x-2 cursor-col-resize touch-none select-none"
                      />
                    ) : null}
                  </th>
                ))}
                {!hideRowOptions ? <th className="w-0" /> : null}
              </tr>
            ))}
          </thead>
        ) : null}
        <tbody>
          {/* https://github.com/clauderic/dnd-kit/issues/1513 */}
          {/* https://github.com/clauderic/dnd-kit/issues/1417 */}
          <SortableContext key={sortableContextKey} items={dataIds} strategy={verticalListSortingStrategy}>
            {table
              .getRowModel()
              .rows.filter((row) => !row.original.hidden)
              .map((row) => (
                <DraggableRow
                  className={rowClassName}
                  key={row.id}
                  row={row}
                  isInnerTable={isInnerTable}
                  hideDragHandle={hideDragHandle}
                  hideRowOptions={hideRowOptions}
                  options={options}
                  duplicateRowAction={duplicateRowAction}
                  deleteRowAction={deleteRowAction}
                  props={{ cell: { ...props?.cell } }}
                  {...rowProps}
                />
              ))}
          </SortableContext>
        </tbody>
      </table>
    </DndContext>
  );
};

export const Table = memo(ITable) as typeof ITable;
